import { TextButton } from 'src/styles/button.style';
import TradeNameSelectionListItem from './TradeNameSelectionListItem';
import { ReactNode, useState, useRef, useEffect } from 'react';
import { Product } from 'src/types/ViewModel/Stowage/Product/Product';
import { useRotationStore } from 'src/context/RotationContext';
import { Container, FilterInputStyle, PaginationContainer } from './TradeNameSelectionListItem.style';

interface TradeNameSelectionProps {
    onClick?: (ev: any, product: Product) => void;
}

const ITEMS_PER_PAGE = 10;

const sortProducts = (products: Product[]) => {
    return [...products].sort((a, b) => {
        const tradeNameComparison = a.tradeName.localeCompare(b.tradeName);
        if (tradeNameComparison !== 0) return tradeNameComparison;

        const categoryComparison = a.category.name.localeCompare(b.category.name);
        if (categoryComparison !== 0) return categoryComparison;

        return (a.chartererName || '').localeCompare(b.chartererName || '');
    });
};

const filterProducts = (products: Product[], filterValue: string) => {
    return products.filter(
        (product) =>
            product.tradeName.toLowerCase().includes(filterValue) ||
            product.category.name.toLowerCase().includes(filterValue) ||
            (product.chartererName?.toLowerCase() || '').includes(filterValue)
    );
};

const TradeNameSelection = ({ onClick }: TradeNameSelectionProps): ReactNode => {
    const { productList } = useRotationStore();
    const [filteredProductList, setFilteredProductList] = useState<Product[]>(sortProducts(productList));
    const [filterValue, setFilterValue] = useState('');
    const filterInputRef = useRef<HTMLInputElement>(null);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = Math.ceil(filteredProductList.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const visibleProducts = filteredProductList.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    useEffect(() => {
        filterInputRef.current?.focus();
    }, []);

    useEffect(() => {
        // Reset selected index when page or filtered list changes
        setSelectedIndex(-1);
    }, [currentPage, filteredProductList]);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setSelectedIndex((prevIndex) => {
                    const newIndex = prevIndex < visibleProducts.length - 1 ? prevIndex + 1 : prevIndex;
                    return newIndex;
                });
                break;

            case 'ArrowUp':
                event.preventDefault();
                setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));
                break;

            case 'Enter':
                event.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < visibleProducts.length) {
                    onClick?.(event, visibleProducts[selectedIndex]);
                }
                break;

            case 'Escape':
                // Optional: close dropdown or clear selection
                setSelectedIndex(-1);
                break;

            default:
                break;
        }
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        setSelectedIndex(-1);
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        if (totalPages <= 3) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        // Always add first page
        pageNumbers.push(1);

        // Add ellipsis after first page if needed
        if (currentPage > 3) {
            pageNumbers.push('...');
        }

        // Add previous page if not first or second page
        if (currentPage > 2) {
            pageNumbers.push(currentPage - 1);
        }

        // Add current page if not first or last
        if (currentPage !== 1 && currentPage !== totalPages) {
            pageNumbers.push(currentPage);
        }

        // Add next page if not last or second-to-last page
        if (currentPage < totalPages - 1) {
            pageNumbers.push(currentPage + 1);
        }

        // Add ellipsis before last page if needed
        if (currentPage < totalPages - 2) {
            pageNumbers.push('...');
        }

        // Always add last page if not already included
        if (!pageNumbers.includes(totalPages)) {
            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newFilterValue = event.target.value.toLowerCase();
        setFilterValue(newFilterValue);
        const filtered = sortProducts(filterProducts(productList, newFilterValue));
        setFilteredProductList(filtered);
        setCurrentPage(1);
        setSelectedIndex(-1);
    };

    const handleProductItemClick = (e: React.MouseEvent, product: Product) => {
        onClick?.(e, product);
    };

    const filterInput = (
        <div>
            <FilterInputStyle
                ref={filterInputRef}
                type="text"
                onChange={handleFilterChange}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                name="filterInput"
                spellCheck="false"
                aria-label="Filter trade names"
            />
        </div>
    );

    return (
        <Container>
            {filterInput}
            <div>
                {visibleProducts.map((product, index) => (
                    <TradeNameSelectionListItem
                        key={'tradeNameSelectionListItem-' + product.id}
                        product={product}
                        onClick={(e) => handleProductItemClick(e, product)}
                        filterValue={filterValue}
                        isSelected={index === selectedIndex}
                    />
                ))}
            </div>

            {totalPages > 1 && (
                <PaginationContainer>
                    <TextButton onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                        Previous
                    </TextButton>

                    {getPageNumbers().map((pageNum, index) => (
                        <TextButton
                            key={index}
                            onClick={() => (typeof pageNum === 'number' ? handlePageChange(pageNum) : undefined)}
                            isSelected={currentPage === pageNum}
                            disabled={typeof pageNum !== 'number'}
                        >
                            {pageNum}
                        </TextButton>
                    ))}

                    <TextButton onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                        Next
                    </TextButton>
                </PaginationContainer>
            )}
        </Container>
    );
};

export default TradeNameSelection;
